import React from "react";
import { ArrowLeft, BadgeCheck, BookOpen, ChevronRight, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getShortVersion } from "../utils/version";

const MORE_ITEMS = [
  { to: "/farmmark", key: "farmmark", Icon: BadgeCheck },
  { to: "/north-star", key: "northStar", Icon: Compass },
];

export default function MoreNew() {
  const { t } = useTranslation("common");
  const text = (key, fallback) => t(key, { defaultValue: fallback });
  const version = getShortVersion();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 mobile-nav-safe-padding">
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex h-12 max-w-4xl items-center gap-2 px-3 sm:px-4">
          <Link
            to="/me"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
            aria-label={text("back", "Back")}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">
            {text("moreNew.title", "More new")}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-4xl space-y-4 px-3 py-5 sm:px-4">
        <section className="grid gap-3 sm:grid-cols-2" aria-label={text("moreNew.title", "More new")}>
          {MORE_ITEMS.map(({ to, key, Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex min-h-[140px] flex-col rounded-xl border border-gray-200 bg-white p-4 transition hover:border-green-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:border-neutral-800 dark:bg-neutral-900"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white">
                    {text(`moreNew.${key}.title`, key === "northStar" ? "NorthStar" : "FarmMark")}
                  </h2>
                  <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                {text(
                  `moreNew.${key}.description`,
                  key === "northStar"
                    ? "Explore Orrith's mission and impact."
                    : "Track impact, leaderboards, and carbon savings."
                )}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition group-hover:translate-x-1 group-hover:text-green-600" aria-hidden="true" />
              </div>
            </Link>
          ))}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
          <div className="flex items-start gap-3">
            <BookOpen className="mt-0.5 h-5 w-5 text-green-700 dark:text-green-400" aria-hidden="true" />
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {text("moreNew.versionNotes.title", "Orrith version notes")}
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {text("moreNew.versionNotes.version", `Version ${version}`)}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                {text(
                  "moreNew.versionNotes.summary",
                  "Orrith brings together trusted marketplace tools, impact tracking, and support for farmers and buyers."
                )}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
